// ─────────────────────────────────────────────────────────────────────────────
// THE VAT RATE OF AN INVOICE LINE, AND THE ONE "VAT REGISTERED" SETTING (item 34): the browser's
// copy of "Aidey Backend/lib/vatRates.js", deliberately, because the browser cannot require a
// backend file. Both are pinned to "Aidey Backend/ci/invoice_amount_cases.json" through
// invoiceAmounts.test.js here and tests/invoiceAmountCases.test.js there. The server is the one
// that refuses (normaliseLines); this copy only shows and previews.
// ─────────────────────────────────────────────────────────────────────────────

export const RATE_TYPES = {
  standard:      { percent: 20, label: '20%',               note: null },
  reduced:       { percent: 5,  label: '5%',                note: null },
  zero:          { percent: 0,  label: '0%',                note: 'Zero-rated' },
  exempt:        { percent: 0,  label: 'Exempt',            note: 'Exempt from VAT' },
  outside_scope: { percent: 0,  label: 'Outside the scope', note: 'Outside the scope of VAT' },
};
export const RATE_ORDER = ['standard', 'reduced', 'zero', 'exempt', 'outside_scope'];

// What a person picks from, in this order, everywhere a rate is chosen.
export const RATE_CHOICES = [
  { value: 'standard',      label: '20% standard' },
  { value: 'reduced',       label: '5% reduced' },
  { value: 'zero',          label: '0% zero-rated' },
  { value: 'exempt',        label: 'Exempt' },
  { value: 'outside_scope', label: 'Outside the scope' },
];

export const STAGGERS = [1, 2, 3];

export function isRateType(v) { return typeof v === 'string' && Object.prototype.hasOwnProperty.call(RATE_TYPES, v); }

/** The rate a line carries: { type, percent, label, note }. type is null for a line written before item 34. */
export function lineRate(line) {
  const l = line && typeof line === 'object' ? line : {};
  if (isRateType(l.rateType)) return { type: l.rateType, ...RATE_TYPES[l.rateType] };
  const n = Number(l.tax);
  const percent = Number.isFinite(n) ? n : 0;
  return { type: null, percent, label: `${percent}%`, note: null };
}

/** The one type a percent can only mean (20 standard, 5 reduced), else null: 0 is never guessed. */
export function rateTypeForPercent(percent) {
  const n = Number(percent);
  if (n === 20) return 'standard';
  if (n === 5) return 'reduced';
  return null;
}

/** The business's VAT settings, from the supplier's users.preferences. */
export function vatSettings(prefs) {
  const p = prefs && typeof prefs === 'object' ? prefs : {};
  const vatNumber = typeof p.vatNumber === 'string' ? p.vatNumber.trim() : '';
  // ONE setting. Before it existed the Tax summary kept its own toggle (taxVatRegistered, which
  // defaulted to registered) and a VAT number could be typed on two screens. Until she saves the new
  // setting, a business counts as registered only when both said so: a VAT number is set and the old
  // toggle was not turned off. Never registered without a number: the number is what makes it valid.
  const registered = typeof p.vatRegistered === 'boolean'
    ? p.vatRegistered && Boolean(vatNumber)
    : (p.taxVatRegistered !== false && Boolean(vatNumber));
  const defaultRate = isRateType(p.vatDefaultRate) ? p.vatDefaultRate : 'standard';
  const stagger = STAGGERS.includes(Number(p.vatStagger)) ? Number(p.vatStagger) : 1;
  return { registered, vatNumber: registered ? vatNumber : '', defaultRate, stagger };
}

/** The rate fields an automatically written line takes: the default when registered, none when not. */
export function defaultLineRate(settings) {
  if (!settings || !settings.registered) return { tax: 0 };
  const type = isRateType(settings.defaultRate) ? settings.defaultRate : 'standard';
  return { rateType: type, tax: RATE_TYPES[type].percent };
}
